import { useEffect } from 'react';
import { logger } from '@garnish/logger';
import { useSelector } from '@xstate5/react';
import { useUpdates } from 'expo-updates';
import { type ActorRefFrom } from 'xstate5';

import { type otaMachine } from './ota-machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook that observes and manages an OTA machine's state based on certain
 * parameters and conditions.
 * It interacts with an actor reference to control the state machine and reacts
 * to updates and enabled status.
 *
 * - It uses multiple effects to manage the lifecycle of an OTA state machine.
 * - It logs the current machine state and decides whether to start or stop the
 * machine based on the `isEnabled` parameter.
 * - It also notifies the machine of any initial updates.
 */
export const useOtaMachineObserver = (params: UseOtaMachineObserverParams) => {
  const { actorRef, isEnabled, logger = defaultLogger } = params;

  const { downloadedUpdate } = useUpdates();

  // ─── State ───────────────────────────────────────────────────────────

  const state = useSelector(actorRef, (snapshot) =>
    JSON.stringify(snapshot.value),
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    logger.debug('Machine state', state);
  }, [logger, state]);

  // Start/stop the machine if the feature is enabled
  useEffect(() => {
    actorRef.send({ type: isEnabled ? 'start' : 'stop' });
  }, [isEnabled, actorRef]);

  // Notify machine about available update
  useEffect(() => {
    if (!isEnabled || !downloadedUpdate) return;

    actorRef.send({ type: 'notify-about-update' });
  }, [downloadedUpdate, isEnabled, actorRef]);
};

// ─── Logger ──────────────────────────────────────────────────────────────────

logger.enable('USE_OTA_MACHINE_OBSERVER');

const defaultLogger = logger.extend('USE_OTA_MACHINE_OBSERVER');

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOtaMachineObserverParams = {
  isEnabled: boolean;
  actorRef: ActorRefFrom<typeof otaMachine>;
  logger?: ReturnType<typeof logger.extend>;
};
